import {
  Button,
  Input,
  InputRef,
  Space,
  Typography,
  Table,
  Select,
  Tag,
  Radio,
  DatePicker,
  Modal,
  Tooltip,
  Checkbox,
  Pagination
} from 'antd'
import React from 'react'
import { createPortal } from 'react-dom'
import { CheckCircleFilled, DownloadOutlined, FilterFilled, FilterOutlined, SearchOutlined, UnorderedListOutlined } from '@ant-design/icons'
import { AxiosError } from 'axios'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import classNames from 'classnames'
import { MenuInfo } from 'rc-menu/lib/interface'
import qs from 'qs'
import dayjs from 'dayjs'
import debounce from 'lodash.debounce'
import { ColumnsType } from 'antd/es/table'
import { MainTitle } from 'components/ui/MainTitle/MainTitle'
import { MobileFiltersDrawer } from 'components/ui/MobileFiltersDrawer/MobileFiltersDrawer'
import { StatusDropdown } from 'components/ui/StatusDropdown/StatusDropdown'
import { EOrderPaymentMethod, EOrderStatus, ERecieveMethod, IMultiOrderRow, IOrdersQueryFilters, ITableOrder } from 'models/orders'
import { getOrderPath } from 'routing/utils/getOrderPath'
import { showServerError } from 'shared/utils/showServerError'
import { ISeller } from 'models/seller'
import { sellerApi } from 'api/seller'
import { ordersApi } from 'api/orders'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { highlightSearchText } from 'shared/utils/highlightSearchText'
import { getOrderStatusItems, multiOrderTags, orderTags } from 'shared/constants/orders'
import { phoneMaskStatic } from 'shared/utils/phoneMaskStatic'
import { superAdminRoutes } from 'shared/constants/routes'
import { appSelectors } from 'store/app'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { ERoles } from 'models/auth'
import { IDateFilter } from 'models/app'
import { acceptOrderModal } from 'shared/utils/orders/acceptOrderModal'
import { pickOrderTimeline } from 'shared/utils/orders/pickOrderTimeline'
import { showSellerDisabledModal } from 'shared/utils/orders/sellerDisabledModal'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import { cleanObject } from 'shared/utils/cleanObject'
import { DEBOUNCE_DELAY } from 'shared/constants/app'
import localeRu from 'antd/es/date-picker/locale/ru_RU'
import { RefundFlowModal } from '../RefundFlowModal/RefundFlowModal'
import { SuperOrderModal } from '../SuperOrder/SuperOrderModal'
import { OrderDeadline } from '../OrderDeadline/OrderDeadline'
import { OrderDateTime } from '../OrderDateTime/OrderDateTime'
import styles from './OrdersList.module.scss'
import 'dayjs/locale/ru'

interface IOrdersListProps {
  sellerId?: string
  // Заказы одного покупателя — для карточки пользователя
  userId?: string
}

type TPriceOrder = 'asc' | 'desc' | undefined

const HINT_OFFSET = 28
const HINT_WIDTH = 250
// Кнопка встаёт серединой на курсор, поэтому высота нужна близкая к реальной
const HINT_HEIGHT = 40
// Пауза перед скрытием: за неё курсор успевает дойти со строки до самой кнопки
const HINT_HIDE_DELAY = 250
// Кнопка ждёт, пока курсор остановится, и не мелькает при проезде по списку
const HINT_SHOW_DELAY = 350
// Совпадает с длительностью анимации ухода в стилях
const HINT_FADE_DURATION = 200

type THintState = 'hidden' | 'visible' | 'closing'

// const getBuyerName = (item: ITableOrder): React.ReactNode => {
//   if (item.delivery_info?.full_name) {
//     return <Typography.Text>{item.delivery_info.full_name}</Typography.Text>
//   }
//   if (item.user_id) {
//     if (item.user_id.name) {
//       return <Typography.Text>{item.user_id.name}</Typography.Text>
//     }
//     return (
//       <Typography.Text style={{ opacity: 0.5 }}>
//         Не заполненный профиль
//       </Typography.Text>
//     )
//   }
//   return (
//     <Typography.Text style={{ opacity: 0.5 }}>
//       Удаленный аккаунт
//     </Typography.Text>
//   )
// }

export const OrdersList: React.FC<IOrdersListProps> = ({ sellerId, userId }) => {
  const role = useAppSelector(appSelectors.selectRole)
  const isSuperAdmin = role === ERoles.ADMIN

  const isMobile = useIsMobile()

  const navigate = useNavigate()

  // Без телефона аккаунт не удалён, а заведён по почте — показываем её.
  // На телефоне номер — ссылка: тап звонит, а заказ открывает остальная ячейка
  const renderBuyer = (user?: ITableOrder['user_id'], callable = false) => {
    if (!user?._id) {
      return <Typography.Text style={{ opacity: 0.5 }}>Удаленный аккаунт</Typography.Text>
    }
    const name = [user.name, user.surname].filter(Boolean).join(' ')
    const phone = user.tel ? phoneMaskStatic(user.tel) : ''
    return (
      <>
        {name && <Typography.Text style={{ display: 'block' }}>{name}</Typography.Text>}
        {phone && callable ? (
          <a
            href={`tel:${phone.replace(/[^\d+]/g, '')}`}
            className={styles.mobilePhone}
            onClick={(event) => event.stopPropagation()}
          >
            {phone}
          </a>
        ) : (
          <Typography.Text>
            {phone ? highlightSearchText(phone, globalSearch) : user.email || 'Без телефона'}
          </Typography.Text>
        )}
      </>
    )
  }
  const location = useLocation()
  const [, setSearchParams] = useSearchParams()
  // В карточке пользователя список — часть страницы: каждая запись в адрес
  // добавила бы шаг в историю, и «назад» возвращал бы на ту же карточку
  const saveSearchParams: typeof setSearchParams = (...args) => {
    if (!userId) {
      setSearchParams(...args)
    }
  }

  const [isFirstRequestFinished, setIsFirstRequestFinished] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [orders, setOrders] = React.useState<ITableOrder[]>([])
  const [superOrders, setSuperOrders] = React.useState<IMultiOrderRow[]>([])
  const [onlySuperOrders, setOnlySuperOrders] = React.useState(false)
  const [sellers, setSellers] = React.useState<ISeller[]>([])

  const [refundModal, setRefundModal] = React.useState<{
    open: boolean
    status: EOrderStatus
    orderId?: string
  }>({ open: false, status: EOrderStatus.CANCELED })
  const [selectedOrderId, setSelectedOrderId] =
    React.useState<string | null>(null)

  const [globalSearch, setGlobalSearch] = React.useState('')

  const [sortDate, setSortData] = React.useState<IDateFilter | null>(null)
  const [datePickerValue, setDatePickerValue] = React.useState<any>(null)

  const [orderNumberSearch, setOrderNumberSearch] = React.useState('')
  const orderNumberSearchRef = React.useRef<InputRef>(null)

  const [sellerSearch, setSellerSearch] = React.useState<string | undefined>(
    undefined
  )
  const [selectedSellerId, setSelectedSellerId] = React.useState('')

  const [phoneBuyerSearch, setPhoneBuyerSearch] = React.useState('')
  const phoneBuyerSearchRef = React.useRef<InputRef>(null)

  const [priceOrder, setPriceOrder] = React.useState<TPriceOrder>(undefined)

  const [isExporting, setIsExporting] = React.useState(false)

  const [isFiltersOpen, setIsFiltersOpen] = React.useState(false)

  const [status, setStatus] = React.useState<EOrderStatus | undefined>(
    undefined
  )

  const [currentPage, setCurrentPage] = React.useState(1)
  const [total, setTotal] = React.useState(0)
  const pageSize = 10

  const getOrders = async (skip: number, status: EOrderStatus | undefined, priceOrder: TPriceOrder, data?: IOrdersQueryFilters) => {
    try {
      setIsLoading(true)
      const response = await ordersApi.getOrders({
        skip: String(skip),
        limit: String(pageSize),
        name: data?.name ?? globalSearch,
        from: data?.from ?? sortDate?.from,
        to: data?.to ?? sortDate?.to,
        number: data?.number ?? orderNumberSearch,
        tel: data?.tel ?? phoneBuyerSearch,
        seller_id: sellerId ?? data?.seller_id ?? selectedSellerId,
        user_id: userId,
        sum: priceOrder,
        status,
        // Супер-заказ не должен разрываться между страницами — на телефоне тоже
        super_orders: 'true'
      })
      setTotal(response.data.count)
      setOrders(response.data.orders)
      saveSearchParams(cleanObject({
        page: skip / pageSize + 1 === 1 ? '' : String(skip / pageSize + 1),
        name: data?.name ?? globalSearch,
        from: data?.from ?? sortDate?.from ?? '',
        to: data?.to ?? sortDate?.to ?? '',
        number: data?.number ?? orderNumberSearch,
        tel: data?.tel ?? phoneBuyerSearch,
        seller_id: data?.seller_id ?? selectedSellerId ?? '',
        sum: priceOrder ?? '',
        status: status ?? ''
      }))
    } catch (e) {
      const error = e as AxiosError
      if (
        (error.response?.status === 500 ||
          error.response?.status === 400 ||
          error.response?.status === 404) && isSuperAdmin && !userId
      ) {
        return navigate(superAdminRoutes.SELLERS)
      }
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  const getSuperOrders = async (skip: number, priceOrder: TPriceOrder, data?: IOrdersQueryFilters) => {
    try {
      setIsLoading(true)
      const response = await ordersApi.getSuperOrders({
        skip: String(skip),
        limit: String(pageSize),
        name: data?.name ?? globalSearch,
        from: data?.from ?? sortDate?.from,
        to: data?.to ?? sortDate?.to,
        number: data?.number ?? orderNumberSearch,
        tel: data?.tel ?? phoneBuyerSearch,
        user_id: userId,
        sum: priceOrder
      })
      setTotal(response.data.count)
      setSuperOrders(response.data.multiOrders)
      saveSearchParams(cleanObject({
        page: skip / pageSize + 1 === 1 ? '' : String(skip / pageSize + 1),
        name: data?.name ?? globalSearch,
        from: data?.from ?? sortDate?.from ?? '',
        to: data?.to ?? sortDate?.to ?? '',
        number: data?.number ?? orderNumberSearch,
        tel: data?.tel ?? phoneBuyerSearch,
        sum: priceOrder ?? '',
        super_orders: 'true'
      }))
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  // Заказы и супер-заказы приходят из разных ручек, но делят фильтры и пагинацию
  const loadList = (
    skip: number,
    status: EOrderStatus | undefined,
    priceOrder: TPriceOrder,
    data?: IOrdersQueryFilters,
    superOnly = onlySuperOrders
  ) => {
    if (superOnly) {
      return getSuperOrders(skip, priceOrder, data)
    }
    return getOrders(skip, status, priceOrder, data)
  }

  React.useEffect(() => {
    if (window.location.search && !userId) {
      const params = qs.parse(window.location.search.substring(1))
      if (params.super_orders) {
        setOnlySuperOrders(true)
      }
      if (params.page) {
        setCurrentPage(Number(params.page))
      }
      if (params.name) {
        setGlobalSearch(params.name as string)
      }
      if (params.from || params.to) {
        setSortData({
          from: params?.from as string ?? '',
          to: params?.to as string ?? ''
        })
        setDatePickerValue([
          params?.from ? dayjs(params?.from as string) : '',
          params?.to ? dayjs(params?.to as string) : ''
        ])
      }
      if (params.number) {
        setOrderNumberSearch((params.number as string).replace(/[^0-9]/g, ''))
      }
      if (params.tel) {
        setPhoneBuyerSearch((params.tel as string).replace(/[^0-9]/g, ''))
      }
      if (params.seller_id) {
        setSelectedSellerId(params.seller_id as string)
      }
      if (params.sum) {
        setPriceOrder(params.sum as any)
      }
      if (params.status) {
        setStatus(params.status as EOrderStatus)
      }
    }
    setIsFirstRequestFinished(true)
  }, [])

  React.useEffect(() => {
    if (sellers.length) {
      setSellerSearch(sellers.find(el => el._id === selectedSellerId)?.shop_details?.shop_name)
    }
  }, [sellers])

  const handleExportOrders = async () => {
    try {
      setIsExporting(true)
      const response = await ordersApi.exportOrdersReport({
        dateFrom: sortDate?.from,
        dateTo: sortDate?.to,
        seller_id: sellerId ?? selectedSellerId
      })
      const blobUrl = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = 'orders_report.xlsx'
      link.click()
      window.URL.revokeObjectURL(blobUrl)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsExporting(false)
    }
  }

  const saveFieldValue = (data: IOrdersQueryFilters) => {
    loadList(0, status, priceOrder, data)
    setCurrentPage(1)
  }

  const onPageChange = (current: number) => {
    loadList((current - 1) * pageSize, status, priceOrder)
    setCurrentPage(current)
  }

  const onChangeGlobalSearch = (searchValue: string) => {
    loadList(0, status, priceOrder, { name: searchValue })
    setCurrentPage(1)
  }

  const onChangePriceOrder = (newPriceOrder: TPriceOrder) => {
    loadList(0, status, newPriceOrder)
    setCurrentPage(1)
  }

  const onChangeStatus = (newStatus: EOrderStatus | undefined) => {
    loadList(0, newStatus, priceOrder)
    setCurrentPage(1)
  }

  const onChangeOnlySuperOrders = (checked: boolean) => {
    setOnlySuperOrders(checked)
    setTotal(0)
    setCurrentPage(1)
    loadList(0, status, priceOrder, undefined, checked)
  }

  const debouncedGetOrdersByGlobalSearch = React.useCallback(debounce(onChangeGlobalSearch, DEBOUNCE_DELAY), [sortDate, orderNumberSearch, selectedSellerId, phoneBuyerSearch, priceOrder, status, onlySuperOrders])

  React.useEffect(() => {
    if (isFirstRequestFinished) {
      loadList((currentPage - 1) * pageSize, status, priceOrder)
      setCurrentPage(currentPage)
    }
  }, [isFirstRequestFinished])

  React.useEffect(() => {
    if (isSuperAdmin && !sellerId) {
      const getSellers = async () => {
        try {
          const response = await sellerApi.getSearchSellers()
          setSellers(response.data)
        } catch (e) {
          showServerError(e)
        }
      }
      getSellers()
    }
  }, [])

  const showSuccessModal = () => {
    bodyScrollLock.enable()
    Modal.confirm({
      centered: true,
      title: 'Заказ принят в работу',
      content:
        'Статус в заказе покупателя также сменится на статус “Принят в работу”',
      icon: <CheckCircleFilled style={{ color: '#52C41A' }} />,
      okText: 'Ок',
      cancelText: 'Закрыть',
      onOk() {
        bodyScrollLock.disable()
      },
      onCancel() {
        bodyScrollLock.disable()
      }
    })
  }

  const openOrder = React.useCallback((orderId: string) => {
    navigate(getOrderPath(orderId), {
      state: {
        backPath: `${location.pathname}${location.search}`
      }
    })
  }, [location.pathname, location.search, navigate])

  // orderId держим и после закрытия: иначе модалка успеет опустеть до конца анимации
  const [superOrderModal, setSuperOrderModal] = React.useState<{
    open: boolean
    orderId: string | null
  }>({ open: false, orderId: null })

  const closeSuperOrderModal = () =>
    setSuperOrderModal((prev) => ({ ...prev, open: false }))

  // Супер-заказ виден только админу площадки: у продавца нет ни его страницы,
  // ни соседних заказов — они чужие
  const [superOrderHint, setSuperOrderHint] = React.useState<{
    multiOrderId: string
    orderId: string
    x: number
    y: number
  } | null>(null)
  const [hintState, setHintState] = React.useState<THintState>('hidden')
  const hintStateRef = React.useRef<THintState>('hidden')
  const hideHintTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const showHintTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeHintTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  // Таймеры читают состояние в момент срабатывания, а не на момент подписки
  const changeHintState = (state: THintState) => {
    hintStateRef.current = state
    setHintState(state)
  }

  const cancelHintHide = () => {
    if (hideHintTimer.current) {
      clearTimeout(hideHintTimer.current)
      hideHintTimer.current = null
    }
    if (closeHintTimer.current) {
      clearTimeout(closeHintTimer.current)
      closeHintTimer.current = null
    }
  }

  const cancelHintShow = () => {
    if (showHintTimer.current) {
      clearTimeout(showHintTimer.current)
      showHintTimer.current = null
    }
  }

  const keepHint = () => {
    cancelHintHide()
    if (hintStateRef.current === 'closing') {
      changeHintState('visible')
    }
  }

  const hideHintNow = () => {
    cancelHintHide()
    cancelHintShow()
    setSuperOrderHint(null)
    changeHintState('hidden')
  }

  const hideHintLater = () => {
    cancelHintHide()
    hideHintTimer.current = setTimeout(() => {
      hideHintTimer.current = null
      cancelHintShow()
      if (hintStateRef.current !== 'visible') {
        setSuperOrderHint(null)
        changeHintState('hidden')
        return
      }
      changeHintState('closing')
      closeHintTimer.current = setTimeout(() => {
        closeHintTimer.current = null
        setSuperOrderHint(null)
        changeHintState('hidden')
      }, HINT_FADE_DURATION)
    }, HINT_HIDE_DELAY)
  }

  React.useEffect(
    () => () => {
      cancelHintHide()
      cancelHintShow()
    },
    []
  )

  const onRowMouseEnter = (order: ITableOrder, event: React.MouseEvent) => {
    if (!isSuperAdmin || isMobile) {
      return
    }
    if (!order.multi_order_id) {
      hideHintLater()
      return
    }
    keepHint()
    setSuperOrderHint({
      multiOrderId: order.multi_order_id,
      orderId: order._id,
      x: Math.min(event.clientX + HINT_OFFSET, window.innerWidth - HINT_WIDTH),
      y: Math.min(
        Math.max(event.clientY - HINT_HEIGHT / 2, 0),
        window.innerHeight - HINT_HEIGHT
      )
    })
    // Отсчёт не перезапускаем на каждой строке: иначе при движении по заказам
    // одного супер-заказа кнопка не появится никогда
    if (hintStateRef.current === 'hidden' && !showHintTimer.current) {
      showHintTimer.current = setTimeout(() => {
        showHintTimer.current = null
        changeHintState('visible')
      }, HINT_SHOW_DELAY)
    }
  }

  const changeStatus = async (
    status: EOrderStatus,
    orderId?: string,
    cancelReason?: string,
    options?: { throwOnError?: boolean }
  ): Promise<boolean> => {
    try {
      setIsLoading(true)
      const normalizedOrderId = orderId ?? selectedOrderId ?? ''
      const { data } = await ordersApi.changeStatus({
        status,
        orderId: normalizedOrderId,
        reason_of_cancel: cancelReason
      })
      setOrders((prev) =>
        prev.map((el) => {
          if (el._id === normalizedOrderId) {
            return {
              ...el,
              status,
              ...pickOrderTimeline(data)
            }
          }
          return el
        })
      )
      setSelectedOrderId(null)
      if (role === ERoles.USER && status === EOrderStatus.CONFIRMED_BY_SELLER) {
        showSuccessModal()
      }
      showSellerDisabledModal(data.seller_penalty)
      return true
    } catch (e) {
      // При возврате ошибку показывает сама модалка возврата — пробрасываем её
      if (options?.throwOnError) throw e
      showServerError(e)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const closeRefundModal = () =>
    setRefundModal((prev) => ({ ...prev, open: false }))

  // Возврат/отмена из выпадающего списка статусов: единая модалка возврата
  const handleRefundConfirm = async () => {
    await changeStatus(refundModal.status, refundModal.orderId, undefined, {
      throwOnError: true
    })
  }

  const onTagClick = React.useCallback((e: MenuInfo, orderId: string) => {
    if (e.key === EOrderStatus.CONFIRMED_BY_SELLER) {
      return acceptOrderModal(async () => {
        await changeStatus(EOrderStatus.CONFIRMED_BY_SELLER, orderId)
        bodyScrollLock.disable()
      })
    }

    if (e.key === EOrderStatus.CANCELED) {
      return setRefundModal({ open: true, status: EOrderStatus.CANCELED, orderId })
    }

    if (e.key === EOrderStatus.RETURNED) {
      return setRefundModal({ open: true, status: EOrderStatus.RETURNED, orderId })
    }

    changeStatus(e.key as EOrderStatus, orderId)
  }, [])

  const normalizedSellers = React.useMemo(() => {
    return sellers.map((item) => ({
      label: item.shop_details.shop_name,
      value: item.shop_details.shop_name,
      key: item._id
    }))
  }, [sellers])

  const desktopColumns: ColumnsType<ITableOrder> = React.useMemo(
    () => {
      const baseColumns: ColumnsType<ITableOrder> = [
        {
          title: isSuperAdmin ? 'ID' : 'Номер заказа',
          dataIndex: 'number',
          width: isSuperAdmin ? sellerId || userId ? '17%' : '12%' : '25%',
          onCell: ({ _id }) => {
            return {
              onClick: () => openOrder(_id)
            }
          },
          render: (_, { number }) => {
            return (
              <Typography.Text style={{ wordWrap: 'break-word' }}>
                {highlightSearchText(number, globalSearch)}
              </Typography.Text>
            )
          },
          filterDropdown: ({ close }) => {
            return (
              <div
                className='filterWrapper'
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Input
                  ref={orderNumberSearchRef}
                  value={orderNumberSearch}
                  onChange={(e) => setOrderNumberSearch(e.target.value.replace(/[^0-9]/g, ''))}
                  onPressEnter={() => {
                    saveFieldValue({ number: orderNumberSearch })
                    close()
                  }}
                  placeholder='Номер заказа'
                  className='filterInput'
                />
                <div className='filterFooterButtons'>
                  <Button
                    type='primary'
                    onClick={() => {
                      saveFieldValue({ number: orderNumberSearch })
                      close()
                    }}
                    icon={<SearchOutlined />}
                    size='small'
                    className='filterButton'
                  >
                    Найти
                  </Button>
                  <Button
                    onClick={() => {
                      setOrderNumberSearch('')
                      saveFieldValue({ number: '' })
                      close()
                    }}
                    size='small'
                    className='filterButton'
                  >
                    Очистить
                  </Button>
                  <Button
                    type='link'
                    size='small'
                    onClick={() => {
                      close()
                    }}
                  >
                    Закрыть
                  </Button>
                </div>
              </div>
            )
          },
          onFilterDropdownOpenChange: (visible) => {
            if (visible) {
              setTimeout(() => orderNumberSearchRef.current?.select(), 100)
            }
          },
          filterIcon: () => (
            <SearchOutlined
              style={{
                color: orderNumberSearch ? '#1677ff' : 'rgba(0, 0, 0, 0.29)'
              }}
            />
          )
        },
        {
          title: 'Дата и время',
          dataIndex: 'createdAt',
          width: isSuperAdmin ? sellerId || userId ? '18%' : '15%' : '25%',
          onCell: ({ _id }) => {
            return {
              onClick: () => openOrder(_id)
            }
          },
          render: (_, record) => <OrderDateTime value={record.createdAt} />
        },
        {
          title: 'Сумма',
          dataIndex: 'full_sum',
          // в админке шире исходных 16% (за счёт «Продавца») — под промокод рядом с суммой
          width: isSuperAdmin ? sellerId || userId ? '20%' : '21%' : '25%',
          sorter: true,
          sortOrder: priceOrder === 'asc' ? 'ascend' : priceOrder === 'desc' ? 'descend' : undefined,
          onCell: ({ _id }) => {
            return {
              onClick: () => openOrder(_id)
            }
          },
          render: (_, { full_sum, promo_details }) => {
            // Админ видит все промокоды, продавец — только компенсируемые продавцом
            const isPromoVisible =
              promo_details?.is_applied &&
              promo_details.promo_code &&
              (isSuperAdmin || promo_details.discount_charge_type === 'SELLER')
            return (
              // nowrap — промокод не переносится на вторую строку; без stopPropagation,
              // чтобы клик по тексту открывал заказ (поведение main)
              <Typography.Text style={{ whiteSpace: 'nowrap' }}>
                {highlightSearchText(getNormalizedPrice(full_sum), globalSearch)}
                {isPromoVisible && (
                  <Tag color='green' style={{ marginLeft: 8, marginRight: 0 }}>
                    {promo_details?.promo_code}
                  </Tag>
                )}
              </Typography.Text>
            )
          }
        },
        {
          title: 'Статус',
          dataIndex: 'status',
          width: isSuperAdmin ? '15%' : '25%',
          onCell: ({ _id }) => {
            return {
              onClick: () => openOrder(_id)
            }
          },
          render: (_, order) => {
            const { status, _id, receive_method } = order
            if (
              status === EOrderStatus.CANCELED ||
              status === EOrderStatus.RETURNED ||
              status === EOrderStatus.NOT_PAID ||
              (status === EOrderStatus.FINISHED && !isSuperAdmin) ||
              (status === EOrderStatus.CONFIRMED_BY_SELLER &&
                receive_method !== ERecieveMethod.PICKUP_POINT &&
                receive_method !== ERecieveMethod.OWN_DELIVERY &&
                !isSuperAdmin) ||
              (receive_method === ERecieveMethod.EXPRESS &&
                status !== EOrderStatus.IN_DELIVERY &&
                status !== EOrderStatus.WAITING_IN_POINT)
            ) {
              return (
                <>
                  <Tag color={orderTags[status].color}>
                    {orderTags[status].label}
                  </Tag>
                  <OrderDeadline order={order} className={styles.deadline} />
                </>
              )
            }
            return (
              <div onClick={(event) => event.stopPropagation()}>
                <StatusDropdown
                  items={getOrderStatusItems(status, role as ERoles, receive_method)}
                  label={orderTags[status].label}
                  color={orderTags[status].color}
                  onClick={(e) => onTagClick(e, _id)}
                />
                <OrderDeadline order={order} className={styles.deadline} />
              </div>
            )
          },
          filterDropdown: ({ close }) => {
            return (
              <div
                className='filterWrapper'
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Radio.Group
                  onChange={(e) => {
                    setStatus(e.target.value)
                    onChangeStatus(e.target.value)
                    close()
                  }}
                  value={status}
                  style={{ display: 'block', marginBottom: 15 }}
                >
                  <Space direction='vertical' size={15}>
                    {Object.values(orderTags).map((item) => (
                      <Radio key={item.value as string} value={item.value}>
                        <Tag color={item.color}>{item.label}</Tag>
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
                <Button
                  onClick={() => {
                    setStatus(undefined)
                    onChangeStatus(undefined)
                    close()
                  }}
                  size='small'
                  className='filterButton'
                >
                  Очистить
                </Button>
                <Button
                  type='link'
                  size='small'
                  onClick={() => {
                    close()
                  }}
                >
                  Закрыть
                </Button>
              </div>
            )
          },
          filterIcon: () => (
            <FilterFilled
              style={{
                color: status ? '#1677ff' : 'rgba(0, 0, 0, 0.25)'
              }}
            />
          )
        }
      ]
      if (isSuperAdmin) {
        const [numberColumn, ...otherColumns] = baseColumns
        const buyerColumn: ColumnsType<ITableOrder>[0] = {
          title: 'Покупатель',
          dataIndex: 'buyerPhone',
          width: sellerId ? '30%' : '21%',
          onCell: ({ _id }) => {
            return {
              onClick: () => openOrder(_id)
            }
          },
          render: (_, item) => renderBuyer(item?.user_id),
          filterDropdown: ({ close }) => {
            return (
              <div
                className='filterWrapper'
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Input
                  ref={phoneBuyerSearchRef}
                  value={phoneBuyerSearch}
                  onChange={(e) => setPhoneBuyerSearch(e.target.value.replace(/[^0-9]/g, ''))}
                  onPressEnter={() => {
                    saveFieldValue({ tel: phoneBuyerSearch })
                    close()
                  }}
                  placeholder='Покупатель'
                  className='filterInput'
                />
                <div className='filterFooterButtons'>
                  <Button
                    type='primary'
                    onClick={() => {
                      saveFieldValue({ tel: phoneBuyerSearch })
                      close()
                    }}
                    icon={<SearchOutlined />}
                    size='small'
                    className='filterButton'
                  >
                    Найти
                  </Button>
                  <Button
                    onClick={() => {
                      setPhoneBuyerSearch('')
                      saveFieldValue({ tel: '' })
                      close()
                    }}
                    size='small'
                    className='filterButton'
                  >
                    Очистить
                  </Button>
                  <Button
                    type='link'
                    size='small'
                    onClick={() => {
                      close()
                    }}
                  >
                    Закрыть
                  </Button>
                </div>
              </div>
            )
          },
          onFilterDropdownOpenChange: (visible) => {
            if (visible) {
              setTimeout(() => phoneBuyerSearchRef.current?.select(), 100)
            }
          },
          filterIcon: () => (
            <SearchOutlined
              style={{
                color: phoneBuyerSearch ? '#1677ff' : 'rgba(0, 0, 0, 0.29)'
              }}
            />
          )
        }

        if (sellerId) {
          return [
            numberColumn,
            buyerColumn,
            ...otherColumns
          ]
        }
        return [
          numberColumn,
          {
            title: 'Продавец',
            dataIndex: 'seller_id',
            // уже исходных 21% — 5% отданы колонке «Сумма» под промокод
            width: userId ? '30%' : '16%',
            onCell: ({ _id }) => {
              return {
                onClick: () => openOrder(_id)
              }
            },
            render: (_, { seller_id }) => {
              return (
                <Typography.Text
                  style={{
                    opacity: seller_id?.shop_details?.shop_name ? 1 : 0.5
                  }}
                >
                  {seller_id?.shop_details?.shop_name ?? 'Удаленный аккаунт'}
                </Typography.Text>
              )
            },
            filterDropdown: ({ close }) => {
              return (
                <div
                  className='filterWrapper'
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Select
                    value={sellerSearch}
                    onChange={(value) => {
                      setSellerSearch(value)
                      setSelectedSellerId('')
                    }}
                    className='filterInput filterSelect'
                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    placeholder='Выберите продавца'
                    onSelect={(_, item) => setSelectedSellerId(item.key)}
                    options={normalizedSellers}
                    showSearch
                    allowClear
                  />
                  <div className='filterFooterButtons'>
                    <Button
                      type='primary'
                      onClick={() => {
                        saveFieldValue({ seller_id: selectedSellerId })
                        close()
                      }}
                      icon={<SearchOutlined />}
                      size='small'
                      className='filterButton'
                    >
                      Найти
                    </Button>
                    <Button
                      onClick={() => {
                        setSellerSearch(undefined)
                        setSelectedSellerId('')
                        saveFieldValue({ seller_id: '' })
                        close()
                      }}
                      size='small'
                      className='filterButton'
                    >
                      Очистить
                    </Button>
                    <Button
                      type='link'
                      size='small'
                      onClick={() => {
                        close()
                      }}
                    >
                      Закрыть
                    </Button>
                  </div>
                </div>
              )
            },
            filterIcon: () => (
              <FilterFilled
                style={{
                  color: selectedSellerId ? '#1677ff' : 'rgba(0, 0, 0, 0.25)'
                }}
              />
            )
          },
          ...(userId ? [] : [buyerColumn]),
          ...otherColumns
        ]
      }
      return baseColumns
    },
    [
      orders,
      normalizedSellers,
      orderNumberSearch,
      sellerSearch,
      selectedSellerId,
      phoneBuyerSearch,
      status,
      priceOrder,
      globalSearch
    ]
  )

  // На телефоне те же колонки собраны в две: заказ с датой (у админа ещё продавец и
  // покупатель) и сумма со статусом. Рендеры берём из десктопных, чтобы не разъезжались
  const columns: ColumnsType<ITableOrder> = React.useMemo(() => {
    if (!isMobile) {
      return desktopColumns
    }

    const findColumn = (dataIndex: string) =>
      desktopColumns.find((column) => (column as any).dataIndex === dataIndex)

    const numberColumn = findColumn('number')
    const sumColumn = findColumn('full_sum')
    const statusColumn = findColumn('status')
    const sellerColumn = findColumn('seller_id')
    const buyerColumn = findColumn('buyerPhone')

    return [
      {
        ...numberColumn,
        title: 'Заказ',
        width: '55%',
        filterDropdown: undefined,
        filterIcon: undefined,
        render: (value, record, index) => (
          <div className={styles.mobileCell}>
            <span className={styles.mobileNumber}>
              {numberColumn?.render?.(value, record, index) as React.ReactNode}
            </span>
            <Typography.Text type='secondary' className={styles.mobileSubText}>
              <OrderDateTime value={record.createdAt} inline />
            </Typography.Text>
            {sellerColumn && (
              <span className={styles.mobileSubText}>
                {sellerColumn.render?.(record.seller_id, record, index) as React.ReactNode}
              </span>
            )}
            {buyerColumn && (
              <span className={styles.mobileSubText}>
                {renderBuyer(record.user_id, true)}
              </span>
            )}
          </div>
        )
      },
      {
        ...sumColumn,
        title: 'Сумма и статус',
        width: '45%',
        filterDropdown: undefined,
        filterIcon: undefined,
        // сортировка по сумме уехала в шторку — в узкой ячейке стрелки съедали место
        sorter: undefined,
        sortOrder: undefined,
        render: (value, record, index) => (
          <div className={styles.mobileCell}>
            {sumColumn?.render?.(value, record, index) as React.ReactNode}
            {statusColumn?.render?.(record.status, record, index) as React.ReactNode}
          </div>
        )
      }
    ]
  }, [desktopColumns, isMobile])

  // В строке списка приходят только id заказов, а модалка ищет группу по любому из них
  const openSuperOrder = ({ orders }: IMultiOrderRow) => {
    const [firstOrderId] = orders
    if (!firstOrderId) {
      return
    }
    setSuperOrderModal({ open: true, orderId: firstOrderId })
  }

  const superOrderColumns: ColumnsType<IMultiOrderRow> = React.useMemo(() => {
    const openCell = (record: IMultiOrderRow) => ({
      onClick: () => openSuperOrder(record)
    })

    const numberCell = ({ number }: IMultiOrderRow) => (
      <Typography.Text style={{ wordWrap: 'break-word' }}>
        {highlightSearchText(number, globalSearch)}
      </Typography.Text>
    )

    const buyerCell = ({ user_id }: IMultiOrderRow) => renderBuyer(user_id)

    const sumCell = ({ sum }: IMultiOrderRow) => (
      <Typography.Text style={{ whiteSpace: 'nowrap' }}>
        {highlightSearchText(getNormalizedPrice(sum), globalSearch)}
      </Typography.Text>
    )

    const statusCell = ({ status, payment_method }: IMultiOrderRow) => (
      <div className={styles.superOrderStatus}>
        <Tag color={multiOrderTags[status]?.color}>
          {multiOrderTags[status]?.label ?? status}
        </Tag>
        {payment_method === EOrderPaymentMethod.INVOICE && (
          <Tag color='blue'>По счёту</Tag>
        )}
      </div>
    )

    if (isMobile) {
      return [
        {
          title: 'Супер-заказ',
          dataIndex: 'number',
          width: '55%',
          onCell: openCell,
          render: (_, record) => (
            <div className={styles.mobileCell}>
              <span className={styles.mobileNumber}>{numberCell(record)}</span>
              <Typography.Text type='secondary' className={styles.mobileSubText}>
                <OrderDateTime value={record.createdAt} inline />
              </Typography.Text>
              <span className={styles.mobileSubText}>
                Заказов: {record.orders.length}
              </span>
              {!userId && (
                <span className={styles.mobileSubText}>{buyerCell(record)}</span>
              )}
            </div>
          )
        },
        {
          title: 'Сумма и статус',
          dataIndex: 'sum',
          width: '45%',
          onCell: openCell,
          render: (_, record) => (
            <div className={styles.mobileCell}>
              {sumCell(record)}
              {statusCell(record)}
            </div>
          )
        }
      ]
    }

    return [
      {
        title: 'Супер-заказ №',
        dataIndex: 'number',
        width: userId ? '24%' : '18%',
        onCell: openCell,
        render: (_, record) => numberCell(record)
      },
      {
        title: 'Заказов',
        dataIndex: 'orders',
        width: userId ? '14%' : '10%',
        onCell: openCell,
        render: (_, { orders }) => (
          <Typography.Text>{orders.length}</Typography.Text>
        )
      },
      ...(userId
        ? []
        : [
          {
            title: 'Покупатель',
            dataIndex: 'user_id',
            width: '22%',
            onCell: openCell,
            render: (_: unknown, record: IMultiOrderRow) => buyerCell(record)
          }
        ]),
      {
        title: 'Дата и время',
        dataIndex: 'createdAt',
        width: userId ? '20%' : '16%',
        onCell: openCell,
        // у супер-заказов время оплаты пишется только с 25.09.2026 — показываем оформление
        render: (_, { createdAt }) => <OrderDateTime value={createdAt} />
      },
      {
        title: 'Сумма',
        dataIndex: 'sum',
        width: userId ? '22%' : '18%',
        sorter: true,
        sortOrder: priceOrder === 'asc' ? 'ascend' : priceOrder === 'desc' ? 'descend' : undefined,
        onCell: openCell,
        render: (_, record) => sumCell(record)
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        width: userId ? '20%' : '16%',
        onCell: openCell,
        render: (_, record) => statusCell(record)
      }
    ]
  }, [globalSearch, isMobile, priceOrder])

  // Фильтры уехали в шторку и снаружи не видны — о выбранном говорит подсветка кнопки
  const hasActiveFilters = Boolean(
    priceOrder || sortDate || orderNumberSearch || phoneBuyerSearch || selectedSellerId || status
  )

  const applyFilters = () => {
    loadList(0, status, priceOrder)
    setCurrentPage(1)
  }

  const resetFilters = () => {
    setOrderNumberSearch('')
    setPhoneBuyerSearch('')
    setSellerSearch(undefined)
    setSelectedSellerId('')
    setStatus(undefined)
    setPriceOrder(undefined)
    setSortData(null)
    setDatePickerValue(null)
    // очищенные значения передаём явно: состояние обновится только к следующему рендеру
    loadList(0, undefined, undefined, { number: '', tel: '', seller_id: '', from: '', to: '' })
    setCurrentPage(1)
  }

  const priceSort = (
    <Radio.Group
      value={priceOrder ?? ''}
      onChange={(e) => {
        const value = (e.target.value || undefined) as TPriceOrder
        setPriceOrder(value)
        onChangePriceOrder(value)
      }}
    >
      <Space direction='vertical'>
        <Radio value=''>Без сортировки</Radio>
        <Radio value='asc'>Сумма по возрастанию</Radio>
        <Radio value='desc'>Сумма по убыванию</Radio>
      </Space>
    </Radio.Group>
  )

  const datePicker = (
    <DatePicker.RangePicker
      value={datePickerValue}
      rootClassName='myRangePicker'
      className={classNames(styles.headerField, styles.datePicker)}
      size='large'
      format='DD/MM/YYYY'
      placement='bottomLeft'
      locale={{
        ...localeRu,
        lang: {
          ...localeRu.lang,
          rangePlaceholder: ['От', 'До']
        }
      }}
      onCalendarChange={(value) => {
        setDatePickerValue(value)
        const fromValue = value?.[0]?.hour(0).minute(0).second(0).format('YYYY-MM-DDTHH:mm:ss') ?? ''
        const toValue = value?.[1]?.hour(23).minute(59).second(59).format('YYYY-MM-DDTHH:mm:ss') ?? ''
        setSortData({
          from: fromValue,
          to: toValue
        })
        saveFieldValue({ from: fromValue, to: toValue })
      }}
      inputReadOnly
    />
  )

  return (
    <>
      <div style={{ paddingBottom: 80 }}>
        {!isSuperAdmin && <MainTitle text='Ваши заказы' hideOnMobile />}
        {isSuperAdmin && !sellerId && !userId && <MainTitle text='Заказы' hideOnMobile />}
        <div className={styles.header}>
          <Input
            value={globalSearch}
            onChange={(e) => {
              setGlobalSearch(e.target.value)
              debouncedGetOrdersByGlobalSearch(e.target.value)
            }}
            placeholder='Поиск'
            size='large'
            suffix={<SearchOutlined />}
            className={styles.headerField}
          />
          {!isMobile && datePicker}
          {isMobile && (
            <Button
              size='large'
              icon={<FilterOutlined />}
              onClick={() => setIsFiltersOpen(true)}
              className={`${styles.headerField} ${hasActiveFilters ? styles.headerFieldActive : ''}`}
            >
              Сортировка и фильтры
            </Button>
          )}
          {/* Отчёт по одному покупателю не фильтруется — выгрузил бы все заказы */}
          {isSuperAdmin && !userId && (
            <Tooltip title='Скачать отчёт по заказам (.xlsx)'>
              <Button
                size='large'
                icon={<DownloadOutlined />}
                loading={isExporting}
                onClick={handleExportOrders}
              >
                Скачать отчёт
              </Button>
            </Tooltip>
          )}
          {isSuperAdmin && (
            <Checkbox
              checked={onlySuperOrders}
              onChange={(e) => onChangeOnlySuperOrders(e.target.checked)}
              className={styles.superOrdersCheckbox}
            >
              Показать супер-заказы
            </Checkbox>
          )}
        </div>
        {onlySuperOrders ? (
          <Table
            scroll={isMobile ? undefined : { x: 850 }}
            size={isMobile ? 'small' : undefined}
            columns={superOrderColumns}
            dataSource={superOrders}
            loading={isLoading}
            rowKey={(el) => el._id}
            rowClassName='tableRow'
            // Подсказка сортировки antd — портал внутри заголовка: клик по ней
            // всплывает в заголовок и переключает сортировку
            showSorterTooltip={false}
            onChange={(pagination, filters, sort: any, extra) => {
              if (extra.action === 'sort') {
                const value = sort.order === 'ascend'
                  ? 'asc'
                  : sort.order === 'descend'
                    ? 'desc'
                    : undefined
                setPriceOrder(value)
                onChangePriceOrder(value)
              }
            }}
            pagination={false}
          />
        ) : (
          <Table
            scroll={isMobile ? undefined : { x: sellerId || userId ? 640 : 850 }}
            size={isMobile ? 'small' : undefined}
            columns={columns}
            dataSource={orders}
            loading={isLoading}
            rowKey={(el) => el._id}
            rowClassName={(record) =>
              superOrderHint && record.multi_order_id === superOrderHint.multiOrderId
                ? 'tableRow tableRowSuperOrder'
                : 'tableRow'
            }
            onRow={(record) => ({
              onMouseEnter: (event) => onRowMouseEnter(record, event),
              onMouseLeave: hideHintLater
            })}
            showSorterTooltip={false}
            onChange={(pagination, filters, sort: any, extra) => {
              if (extra.action === 'sort') {
                const value = sort.order === 'ascend'
                  ? 'asc'
                  : sort.order === 'descend'
                    ? 'desc'
                    : undefined
                setPriceOrder(value)
                onChangePriceOrder(value)
              }
            }}
            pagination={false}
          />
        )}
        {/* Пагинация отдельно от таблицы: страница бывает длиннее pageSize,
        когда супер-заказ не влез в лимит, а antd режет такой dataSource сам */}
        <div className={styles.pagination}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={total}
            onChange={onPageChange}
            showSizeChanger={false}
          />
        </div>
        {isMobile && (
          <MobileFiltersDrawer
            open={isFiltersOpen}
            onClose={() => setIsFiltersOpen(false)}
            title='Сортировка и фильтры'
            columns={onlySuperOrders ? superOrderColumns : desktopColumns}
            onApply={applyFilters}
            onReset={resetFilters}
            sections={[
              { title: 'Сортировка', content: priceSort },
              { title: 'Период', content: datePicker }
            ]}
          />
        )}
      </div>
      <RefundFlowModal
        open={refundModal.open}
        onConfirm={handleRefundConfirm}
        onClose={closeRefundModal}
      />
      {superOrderHint &&
        hintState !== 'hidden' &&
        createPortal(
          <div
            className={`${styles.superOrderHint} ${
              hintState === 'closing' ? styles.superOrderHintClosing : ''
            }`}
            style={{ left: superOrderHint.x, top: superOrderHint.y }}
            onMouseEnter={keepHint}
            onMouseLeave={hideHintLater}
          >
            <Button
              className={styles.superOrderHintButton}
              icon={<UnorderedListOutlined />}
              onClick={() => {
                setSuperOrderModal({
                  open: true,
                  orderId: superOrderHint.orderId
                })
                hideHintNow()
              }}
            >
              Открыть супер-заказ
            </Button>
          </div>,
          document.body
        )}
      <SuperOrderModal
        open={superOrderModal.open}
        orderId={superOrderModal.orderId}
        onClose={closeSuperOrderModal}
        onOrdersChanged={() =>
          loadList((currentPage - 1) * pageSize, status, priceOrder)
        }
      />
    </>
  )
}
